<template>
  <form class="ui form settings" @submit.prevent="submit">
    <div class="ui grid onboarding-h1-row">
      <div class="sixteen wide column">
        <h1>Job settings</h1>
      </div>
    </div>

    <div class="ui stackable grid onboarding-content-row">
      <div class="sixteen wide mobile eight wide computer column">
        <p class="body-large">
          Smardie is based on a fixed rate price system, which means every task listed has a set time allocated to completing it.
        </p>
        <p class="body-large">
          To accurately price your jobs we need you to tell us your hourly rate and call out fee.
        </p>
        <p class="body-large">
          We also need to know whether you want to show your call out fee or use Smardie's 'smart pricing' feature which incorporates the fee into your quote's line items, allowing you to advertise 'free callouts'.
        </p>
        <p class="body-large">
          You can also set a standard markup percentage for products — this is applied to supplier buy prices so you earn margin on materials, not just labour.
        </p>
      </div>
      <div class="sixteen wide mobile eight wide computer column">
        <div class="field settings">
          <label>Hourly Rate*</label>
          <div class="ui left labeled input">
            <label for="hourlyrate" class="ui label">$</label>
            <input
              id="hourlyrate"
              v-model="form.hourly_rate"
              type="text"
              placeholder="0.00"
              v-bind:class="{ error: errors.hourly_rate }"
              @input="clearErrorWhenValid('hourly_rate')"
            />
          </div>
        </div>
        <div class="field settings">
          <label>Callout fee*</label>
          <div class="ui left labeled input">
            <label for="calloutfee" class="ui label">$</label>
            <input
              id="calloutfee"
              v-model="form.callout_fee"
              type="text"
              placeholder="0.00"
              v-bind:class="{ error: errors.callout_fee }"
              @input="clearErrorWhenValid('callout_fee')"
            />
          </div>
        </div>
        <div class="field settings">
          <label>Pricing Structure</label>
          <SegmentControl
            :options="pricingOptions"
            :model-value="form.price_structure"
            @update:model-value="form.price_structure = $event"
          />
        </div>
        <div class="field settings">
          <label>Product Markup %</label>
          <div class="ui right labeled input">
            <input
              id="standardmarginpct"
              v-model="form.standard_margin_pct"
              type="text"
              placeholder="30.00"
              v-bind:class="{ error: errors.standard_margin_pct }"
              @input="clearErrorWhenValid('standard_margin_pct')"
            />
            <div class="ui label">%</div>
          </div>
        </div>
      </div>
    </div>

    <div class="ui divider onboarding-divider"></div>

    <div class="onboarding-actions">
      <button type="button" class="onboarding-back-btn ui basic button" @click="$router.push('/onboarding/welcome')">Back</button>
      <button
        type="submit"
        class="onboarding-continue-btn ui primary button settings-save-btn"
        :class="{ loading: saving }"
        :disabled="saving"
      >
        Continue
      </button>
    </div>
  </form>
</template>

<script>
import axios from 'axios'
import SegmentControl from '@/components/common/SegmentControl.vue';

export default {
  name: 'OnboardingJobSettings',
  components: { SegmentControl },
  data() {
    return {
      form: {
        hourly_rate: '',
        callout_fee: '',
        standard_margin_pct: '30',
        price_structure: 2
      },
      errors: {},
      saving: false,
      pricingOptions: [
        { value: 1, label: 'Regular Pricing', icon: 'pricing_regular' },
        { value: 2, label: 'Smart Pricing', icon: 'pricing_smart' }
      ]
    };
  },
  created() {
    axios.get('/api/setting/settings').then((res) => {
      this.form.hourly_rate = res.data.hourly_rate != null && res.data.hourly_rate !== '' ? String(res.data.hourly_rate) : '';
      this.form.callout_fee = res.data.callout_fee != null && res.data.callout_fee !== '' ? String(res.data.callout_fee) : '';
      this.form.standard_margin_pct = res.data.standard_margin_pct != null && res.data.standard_margin_pct !== '' ? String(res.data.standard_margin_pct) : '30';
      this.form.price_structure = res.data.price_structure === 1 ? 1 : 2;
    }).catch(() => {});
  },
  methods: {
    clearErrorWhenValid(field) {
      const v = this.form[field];
      if (v === '') return;
      const num = parseFloat(v);
      if (!isNaN(num) && num >= 0) this.$set(this.errors, field, false);
    },
    validate() {
      this.errors = {};
      const hr = parseFloat(this.form.hourly_rate);
      const cf = parseFloat(this.form.callout_fee);
      const mp = parseFloat(this.form.standard_margin_pct);
      if (this.form.hourly_rate === '' || isNaN(hr) || hr < 0) this.errors.hourly_rate = true;
      if (this.form.callout_fee === '' || isNaN(cf) || cf < 0) this.errors.callout_fee = true;
      if (this.form.standard_margin_pct === '' || isNaN(mp) || mp < 0 || mp > 100) this.errors.standard_margin_pct = true;
      return Object.keys(this.errors).length === 0;
    },
    submit() {
      if (!this.validate()) {
        this.errorNotification('Please enter a valid hourly rate, callout fee, and markup %.');
        return;
      }
      this.saving = true;
      axios.put('/api/setting/settings', {
        hourly_rate: parseFloat(this.form.hourly_rate),
        callout_fee: parseFloat(this.form.callout_fee),
        standard_margin_pct: parseFloat(this.form.standard_margin_pct),
        price_structure: this.form.price_structure
      }).then(() => {
        this.$router.push('/onboarding/plan');
      }).catch((err) => {
        this.errorNotification((err.response && err.response.data && err.response.data.error) || 'Failed to save. Please try again.');
      }).finally(() => {
        this.saving = false;
      });
    }
  }
};
</script>

<style scoped>
.onboarding-h1-row {
  margin-bottom: 0;
}
.onboarding-content-row {
  margin-top: 1rem;
}
.onboarding-divider {
  margin: 2rem 0 0 0;
  border-top: 1px solid rgba(34, 36, 38, 0.15);
}
.onboarding-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 0;
}
.onboarding-actions .onboarding-back-btn,
.onboarding-actions .onboarding-continue-btn {
  height: 36px;
  line-height: 36px;
  padding-top: 0;
  padding-bottom: 0;
}
@media (max-width: 767px) {
  .onboarding-actions {
    flex-direction: column;
    gap: 16px;
  }
  .onboarding-actions .onboarding-continue-btn,
  .onboarding-actions .onboarding-back-btn {
    width: 100%;
  }
  .onboarding-actions .onboarding-continue-btn {
    order: 1;
    margin-bottom: 0 !important;
  }
  .onboarding-actions .onboarding-back-btn {
    order: 2;
  }
}
</style>
